import { MigrationCostSummary } from '@amzn/awscat-portfolio-assessment-types';
import { Box, Button, FormField, Input, Modal, NonCancelableCustomEvent, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-react';
import { BaseChangeDetail } from '@amzn/awsui-components-react/polaris/input/interfaces';
import { useEffect, useState } from 'react';

import { constants } from '../../common/Localization';
import { deepCopy } from '../../common/util';
import { getRateCapByResource, MIGRATION_RESOURCE_CAP, RATE } from './constants';
import { SprintTeamResourceData } from './interfaces';

interface Props {
    currency: string;
    visible: boolean;
    setVisible: (visible: boolean) => void;
    migrationCostSummary: MigrationCostSummary[];
    sprintTeamResourceData: SprintTeamResourceData[];
    onRecalculate: (sprintTeamData: SprintTeamResourceData[]) => void;
}

function MigrationSummarySprintTeamResourceEditModal({currency, visible, setVisible, migrationCostSummary, sprintTeamResourceData, onRecalculate}: Props) {

    const [ sprintTeamDataForEditing, setSprintTeamDataForEditing ] = useState(sprintTeamResourceData);
    const [ errorTexts, setErrorTexts ] = useState<{[key: string]: string}>({});

    useEffect(() => {
        setSprintTeamDataForEditing(sprintTeamResourceData);
    }, [sprintTeamResourceData]);

    const getErrorText = (key: string): string => {
        return key in errorTexts ? errorTexts[key] : '';
    };

    const setErrorText = (key: string, errorMsg: string): void => {
        const errors = deepCopy(errorTexts);
        errors[key] = errorMsg;
        setErrorTexts(errors);
    };

    const onInputChanged = (resource: string, strategy: string) => (event: NonCancelableCustomEvent<BaseChangeDetail>) => {
        const newValue = Number(event.detail.value);
        const rateCap = getRateCapByResource(resource);
        if (isNaN(newValue) || event.detail.value.length === 0) {
            setErrorText(resource + strategy, constants.NUMBER_ONLY_MSG);
        } else if (strategy === RATE && newValue > rateCap) {
            setErrorText(resource + strategy, `${constants.FORM_SHOULD_BE_NO_GREATER_THAN} ${rateCap}`);
        } else if (strategy !== RATE && newValue > MIGRATION_RESOURCE_CAP) {
            setErrorText(resource + strategy, `${constants.FORM_SHOULD_BE_NO_GREATER_THAN} ${MIGRATION_RESOURCE_CAP}`);
        } else if (newValue < 0) {
            setErrorText(resource + strategy, `${constants.FORM_SHOULD_BE_NO_LESS_THAN} 0`);
        } else {
            setErrorText(resource + strategy, '');
        }
        const copySprintData: SprintTeamResourceData[] = deepCopy(sprintTeamDataForEditing);
        const sprintTeam = copySprintData.find(sprint => sprint.resource === resource);
        if (sprintTeam) {
            const index = copySprintData.indexOf(sprintTeam);
            if (strategy === RATE) {
                sprintTeam.rate = event.detail.value;
            } else {
                sprintTeam.strategies[strategy] = event.detail.value;
            }
            copySprintData[index] = sprintTeam;
            setSprintTeamDataForEditing(copySprintData);
        }
    };

    const getCellForEditing = (value: string, resource: string, strategy: string) => {
        return (
            <FormField errorText={getErrorText(resource + strategy)}>
                <Input value={value} onChange={onInputChanged(resource, strategy)} />
            </FormField>
        );
    };

    const getSprintTeamResourceColumnDefinitions = (): TableProps.ColumnDefinition<SprintTeamResourceData>[] => {
        const firstColumnDef: TableProps.ColumnDefinition<SprintTeamResourceData> = 
        {
            id: 'resource',
            header: constants.RESOURCE,
            cell: item => item.resource
        };

        const lastColumnDef: TableProps.ColumnDefinition<SprintTeamResourceData> = 
        {
            id: RATE,
            header: `${constants.RATE} (${currency}/${constants.HOUR_ABV})`,
            cell: item => getCellForEditing(item.rate.toString(), item.resource, RATE)
        };
    
        const middleColumns: TableProps.ColumnDefinition<SprintTeamResourceData>[] = migrationCostSummary.map(summary => {
            return {
                id: summary.strategy,
                header: summary.strategy,
                cell: item => getCellForEditing(item.strategies[summary.strategy].toString() ?? '', item.resource, summary.strategy)
            };
        });
    
        return [firstColumnDef, ...middleColumns, lastColumnDef];
    };

    const dismissModal = (): void => {
        setSprintTeamDataForEditing(sprintTeamResourceData);
        setVisible(false);
    };

    const saveAndRecalculate = () => {
        setVisible(false);
        onRecalculate(sprintTeamDataForEditing);
    };

    const isSaveBtnDisabled = (): boolean => {
        return Object.keys(errorTexts).find(key => errorTexts[key].length > 0) ? true : false;
    };    
    
    const content = (
        <Table
            items={sprintTeamDataForEditing}
            columnDefinitions={getSprintTeamResourceColumnDefinitions()}
        />
    );

    const footer = (
        <Box float='right'>
            <SpaceBetween size='s' direction='horizontal'>
                <Button variant='link' onClick={dismissModal}>Cancel</Button>
                <Button variant='primary' onClick={saveAndRecalculate} disabled={isSaveBtnDisabled()}>{constants.APPLY}</Button>
            </SpaceBetween>
        </Box>      
    );

    return (
        <Modal
            visible={visible}
            footer={footer}
            size='max'
            onDismiss={dismissModal}
            header={constants.EDIT_RESOURCES}
        >
            {content}
        </Modal>
    );
}

export default MigrationSummarySprintTeamResourceEditModal;
